// Layer 4 (UI) — the route's status band (locked comp `process.html` A): one
// card, four hairline-split cells, unordered (no direction notch): who is off
// AUTO, for how long against the 4 h rule, which reading is out, what the
// control room is asked. Each cell is a primary figure over its comparison,
// with a 3px state rule only where the fact is an exception. Renders
// Scene.transform.ts `processBand`; derives nothing.

import { GlassCard } from '@/design-system';
import { cn } from '@/shared/cn';
import { MeterBar, vizToneColor } from '@/versions/v1/modules/_viz';
import { ActorChip } from '../advisory/PipelineStrip';
import { CaseCodeChip } from '../quality/QualityUi';
import type { ProcessBandCell } from './Scene.transform';

export function ProcessBand({
  cells,
  onOpenCase,
}: {
  cells: readonly ProcessBandCell[];
  onOpenCase: (caseId: string, title: string) => void;
}) {
  return (
    <GlassCard
      as="section"
      tone="matte"
      aria-label="Loops and readings now"
      data-process-band
      className="overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0"
    >
      <div className="grid grid-cols-2 xl:grid-cols-4">
        {cells.map((cell) => (
          <div
            key={cell.id}
            data-band-cell={cell.id}
            className="relative flex min-h-[7.25rem] min-w-0 flex-col gap-1 border-b border-r border-[var(--hairline)] px-4 pb-3 pt-3 even:border-r-0 xl:border-b-0 xl:even:border-r xl:last:border-r-0"
          >
            {cell.tone !== 'neutral' ? (
              <span aria-hidden data-band-tone={cell.tone} className="absolute inset-x-0 top-0 h-[3px]" style={{ backgroundColor: vizToneColor(cell.tone) }} />
            ) : null}
            <span className="truncate text-xs font-semibold uppercase tracking-[0.07em] text-muted">{cell.name}</span>
            <span data-band-figure={cell.id} className="whitespace-nowrap text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary">
              {cell.figure}
              {cell.unit ? <>{' '}<span className="text-sm font-semibold tracking-normal text-muted">{cell.unit}</span></> : null}
            </span>
            <span data-band-comparison={cell.id} className="text-xs leading-snug tabular-nums text-muted">
              {cell.comparison.map((segment, index) => (
                <span key={index} className={cn(segment.strong && 'font-semibold text-ink-secondary')}>{segment.text}</span>
              ))}
            </span>
            {cell.meter ? (
              <MeterBar
                value={cell.meter.value}
                max={cell.meter.max}
                tone={cell.meter.tone}
                markerCaption={false}
                className="mt-1 [&>div:first-child]:h-1.5"
              />
            ) : null}
            {cell.ask ? (
              <span className="mt-auto flex min-w-0 flex-wrap items-center gap-1.5 pt-1">
                <CaseCodeChip
                  code={cell.ask.item.caseCode}
                  label={cell.ask.item.caseTitle}
                  onOpen={() => onOpenCase(cell.ask!.item.caseId, cell.ask!.item.caseTitle)}
                />
                {cell.ask.open ? <ActorChip actor="Human">{cell.ask.item.targetLabel}</ActorChip> : null}
              </span>
            ) : null}
          </div>
        ))}
      </div>
    </GlassCard>
  );
}
